import {
  readPreviousAnswer,
  isTextAnswer,
  type AnswerJson,
} from "../answerJson";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import { formatCopy } from "../../theme/copy";
import type { newRowWithImage } from "../../util/types";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";

export type TextListRaw = {
  type: "text_list";
  content: string;
  rows: newRowWithImage[];
  min_options: number;
  previous_response?: string[] | string;
  previous_answer_json?: unknown;
  dont_know_option?: { value: string; text: string } | null;
  image?: string;
  alt_text?: string | null;
  error_message?: string;
};

export type TextListDefinition = {
  prompt: string;
  rows: readonly { id: string; label: string; image?: string }[];
  minAnswers: number;
  previousResponses: readonly string[];
  previousDontKnow: boolean;
  dontKnow?: { value: string; label: string };
  image?: string;
  altText?: string;
  error?: string;
};

export type TextListDraft = {
  values: readonly string[];
  activeIndex: number;
  dontKnowSelected: boolean;
};

export type TextListEvent =
  | { type: "set-row"; index: number; value: string }
  | { type: "activate-row"; index: number }
  | { type: "advance" }
  | { type: "toggle-dont-know" };

export type TextListResponse = {
  type: "text_list";
  value: string;
  responses: string;
  answerJson: AnswerJson;
};

const validIndex = (definition: TextListDefinition, index: number) =>
  Number.isInteger(index) && index >= 0 && index < definition.rows.length;

export const textListCore: QuestionCore<
  TextListRaw,
  TextListDefinition,
  TextListDraft,
  TextListEvent,
  TextListResponse
> = {
  decode: (raw) => {
    raw = {
      ...raw,
      previous_response: readPreviousAnswer(
        raw.previous_answer_json,
        raw.previous_response,
        isTextAnswer,
      ),
    };
    if (!Array.isArray(raw.rows) || raw.rows.length === 0) {
      throw new Error("Text list requires at least one row.");
    }
    if (
      !Number.isInteger(raw.min_options) ||
      raw.min_options < 1 ||
      raw.min_options > raw.rows.length
    ) {
      throw new Error("Text list has an invalid minimum response count.");
    }
    const dontKnowValue = raw.dont_know_option?.value;
    const previousDontKnow =
      dontKnowValue !== undefined &&
      (raw.previous_response === dontKnowValue ||
        (Array.isArray(raw.previous_response) &&
          raw.previous_response.length === 1 &&
          raw.previous_response[0] === dontKnowValue));
    if (
      raw.previous_response !== undefined &&
      !previousDontKnow &&
      (!Array.isArray(raw.previous_response) ||
        raw.previous_response.some((value) => typeof value !== "string"))
    ) {
      throw new Error("Text list previous response must be a list.");
    }
    return {
      prompt: raw.content,
      rows: raw.rows.map((row) => ({
        id: row.value,
        label: row.text,
        ...(row.image ? { image: row.image } : {}),
      })),
      minAnswers: raw.min_options,
      previousResponses:
        !previousDontKnow && Array.isArray(raw.previous_response)
          ? raw.previous_response
          : [],
      previousDontKnow,
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => {
    const values = definition.rows.map(
      (_, index) => definition.previousResponses[index] ?? "",
    );
    const firstBlank = values.findIndex((value) => !value.trim());
    return {
      values,
      activeIndex: firstBlank < 0 ? values.length - 1 : firstBlank,
      dontKnowSelected: definition.previousDontKnow,
    };
  },
  update: (definition, draft, event) => {
    if (event.type === "toggle-dont-know") {
      if (!definition.dontKnow) return draft;
      return {
        values: definition.rows.map(() => ""),
        activeIndex: 0,
        dontKnowSelected: !draft.dontKnowSelected,
      };
    }
    if (event.type === "advance") {
      return {
        ...draft,
        activeIndex: Math.min(
          draft.activeIndex + 1,
          definition.rows.length - 1,
        ),
      };
    }
    if (!validIndex(definition, event.index)) return draft;
    if (event.type === "activate-row")
      return { ...draft, activeIndex: event.index };
    const values = [...draft.values];
    values[event.index] = event.value;
    return { ...draft, values, dontKnowSelected: false };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) => {
    if (draft.dontKnowSelected && definition.dontKnow) return { valid: true };
    const answered = draft.values.filter((value) => value.trim()).length;
    return answered >= definition.minAnswers
      ? { valid: true }
      : {
          valid: false,
          error: formatCopy(copy.validation.minResponses, {
            min: definition.minAnswers,
          }),
        };
  },
  encode: (definition, draft) => {
    const validation = textListCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    if (draft.dontKnowSelected && definition.dontKnow) {
      return {
        type: "text_list",
        value: definition.dontKnow.value,
        responses: encodeResponses([definition.dontKnow.value]),
        answerJson: [definition.dontKnow.value],
      };
    }
    return {
      type: "text_list",
      value: "total_list_count_remove",
      responses: encodeResponses(draft.values),
      answerJson: draft.values,
    };
  },
  projectTranscript: (definition, draft) => ({
    kind: "text_list",
    question: definition.prompt,
    answer:
      draft.dontKnowSelected && definition.dontKnow
        ? definition.dontKnow.label
        : definition.rows
            .map((row, index) => `${row.label}. ${draft.values[index]}`)
            .join("\n"),
  }),
};
